import type { DeviceType } from '@gc/shared';

import billiardArt from '@/assets/device-art/billiard.webp';
import cinemaArt from '@/assets/device-art/cinema.webp';
import consoleArt from '@/assets/device-art/console.webp';
import pcArt from '@/assets/device-art/pc.webp';
import tableArt from '@/assets/device-art/table.webp';
import vpcArt from '@/assets/device-art/vpc.webp';

/**
 * Per-type artwork for the Device List tiles — the picture a device shows until
 * someone uploads a photo of that actual machine.
 *
 * Two tiers, both local. The common types ship a product render (bundled, so
 * Vite fingerprints them and the shell cache serves them — a venue server runs
 * on-prem with unreliable internet per SPEC §2, and the board must never wait on
 * a network fetch to render). The rest fall back to inline SVG, which inherits
 * `currentColor` and so needs no second asset set per theme.
 *
 * Renders sit in `assets/device-art/*.png` at 512px as the editable masters;
 * only the WebP exports are bundled.
 */

const TEMPLATE_ART: Partial<Record<DeviceType, string>> = {
  PC: pcArt,
  CONSOLE: consoleArt,
  BILLIARD: billiardArt,
  TABLE: tableArt,
  CINEMA: cinemaArt,
};

/**
 * Categories whose machines are the venue's premium ones — a private room, a
 * VIP booth — and should be recognisable at a glance on a wall of otherwise
 * identical tiles.
 *
 * Matched on the category NAME because that is where a venue already draws the
 * distinction; there is no "premium" flag on a device and inventing one would
 * be a schema change to hold what the category name already says. A venue that
 * renames the category simply gets the standard render back, which is a dull
 * failure rather than a broken one.
 *
 * This is only a default. A photo uploaded against the device still wins, as
 * it does for every other device.
 */
const PREMIUM_CATEGORY = /\b(vip|vpc|private)\b/i;

export function isPremiumCategory(categoryName?: string): boolean {
  return categoryName !== undefined && PREMIUM_CATEGORY.test(categoryName);
}

/**
 * The bundled render for a device type, or null for the types that draw as
 * inline SVG. Exported for the dashboard's floor cards, which need the same
 * "photo wins, template otherwise" rule as the tiles here but compose their
 * own layout around the image.
 */
export function templateArtFor(deviceType: DeviceType, categoryName?: string): string | null {
  if (deviceType === 'PC' && isPremiumCategory(categoryName)) return vpcArt;
  return TEMPLATE_ART[deviceType] ?? null;
}

interface ArtworkProps {
  deviceType: DeviceType;
  /** The device's category, used only to pick the premium render. */
  categoryName?: string;
  className?: string;
}

export function DeviceArtwork({ deviceType, categoryName, className }: ArtworkProps) {
  // Premium PCs get their own render: same treatment as the others — isolated
  // on transparency with a soft shadow — but a purple rig with the room's chair
  // in shot, against the standard blue. An earlier attempt drew this as line
  // art, which sat next to five photographs looking like a missing image.
  const premium = deviceType === 'PC' && isPremiumCategory(categoryName);
  const template = premium ? vpcArt : TEMPLATE_ART[deviceType];
  if (template) {
    return (
      <img
        src={template}
        alt=""
        aria-hidden
        data-device-art={deviceType}
        data-device-art-variant={premium ? 'premium' : undefined}
        className={['object-contain', className].filter(Boolean).join(' ')}
      />
    );
  }

  const shared = {
    viewBox: '0 0 48 48',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
  };

  switch (deviceType) {
    case 'PC':
      return (
        <svg {...shared}>
          {/* monitor */}
          <rect x="7" y="9" width="34" height="22" rx="2" />
          <path d="M20 31v4h8v-4" />
          <path d="M15 35h18" />
          {/* keyboard */}
          <rect x="11" y="38" width="26" height="5" rx="1.5" />
        </svg>
      );

    case 'CONSOLE':
      return (
        <svg {...shared}>
          {/* gamepad */}
          <path d="M16 17h16a9 9 0 0 1 8.6 6.4l2.2 7.4A5 5 0 0 1 34 34l-3.5-4h-13L14 34a5 5 0 0 1-8.8-3.2l2.2-7.4A9 9 0 0 1 16 17Z" />
          <path d="M15 23v5M12.5 25.5h5" />
          <circle cx="32" cy="24" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="35.5" cy="27.5" r="1.4" fill="currentColor" stroke="none" />
        </svg>
      );

    case 'BILLIARD':
      return (
        <svg {...shared}>
          {/* table with pockets */}
          <rect x="5" y="12" width="38" height="24" rx="3" />
          <rect x="9" y="16" width="30" height="16" rx="1.5" />
          <circle cx="9" cy="16" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="39" cy="16" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="9" cy="32" r="1.6" fill="currentColor" stroke="none" />
          <circle cx="39" cy="32" r="1.6" fill="currentColor" stroke="none" />
          {/* balls */}
          <circle cx="19" cy="24" r="2.6" />
          <circle cx="28" cy="21" r="2.6" />
          <circle cx="30" cy="28" r="2.6" />
        </svg>
      );

    case 'TABLE':
      return (
        <svg {...shared}>
          {/* round table seen at an angle, with two chairs */}
          <ellipse cx="24" cy="20" rx="14" ry="7" />
          <path d="M24 27v11" />
          <path d="M17 41h14" />
          <path d="M7 22v7M41 22v7" />
        </svg>
      );

    case 'ARCADE':
      return (
        <svg {...shared}>
          {/* upright cabinet */}
          <path d="M13 6h22a2 2 0 0 1 2 2v32a2 2 0 0 1-2 2H13a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" />
          <rect x="15" y="10" width="18" height="12" rx="1.5" />
          <circle cx="19" cy="28" r="2" />
          <circle cx="27" cy="28" r="1.4" fill="currentColor" stroke="none" />
          <circle cx="31" cy="28" r="1.4" fill="currentColor" stroke="none" />
          <path d="M15 35h18" />
        </svg>
      );

    case 'CINEMA':
      return (
        <svg {...shared}>
          {/* screen + seats */}
          <path d="M6 9h36v20H6z" />
          <path d="M10 36h5v6h-5zM21.5 36h5v6h-5zM33 36h5v6h-5z" />
        </svg>
      );

    default:
      return (
        <svg {...shared}>
          <rect x="9" y="13" width="30" height="22" rx="2.5" />
          <path d="M9 21h30" />
          <circle cx="14" cy="17" r="1.2" fill="currentColor" stroke="none" />
        </svg>
      );
  }
}
